import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, X, Stethoscope, MapPin, User } from 'lucide-react';
import { doctorsApi, locationsApi, usersApi } from '@/api/client';
import { toast } from '@/utils/toast';

const doctorSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    qualification: z.string().optional(),
    registrationNumber: z.string().optional(),
    userId: z.string().optional(),
    locationIds: z.array(z.string()).min(1, 'Select at least one location'),
    status: z.enum(['ACTIVE', 'INACTIVE']),
});

type DoctorFormData = z.infer<typeof doctorSchema>;

export function DoctorsPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDoctor, setEditingDoctor] = useState<any>(null);
    const queryClient = useQueryClient();

    const { data: doctors = [], isLoading } = useQuery({
        queryKey: ['doctors'],
        queryFn: () => doctorsApi.list(),
    });

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const { data: users = [] } = useQuery({
        queryKey: ['users'],
        queryFn: usersApi.list,
    });

    const createMutation = useMutation({
        mutationFn: doctorsApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doctors'] });
            toast.success('Doctor added successfully');
            handleCloseModal();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to add doctor');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<DoctorFormData> }) =>
            doctorsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doctors'] });
            toast.success('Doctor updated successfully');
            handleCloseModal();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update doctor');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: doctorsApi.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doctors'] });
            toast.success('Doctor deactivated successfully');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to deactivate doctor');
        },
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm<DoctorFormData>({
        resolver: zodResolver(doctorSchema),
        defaultValues: {
            name: '',
            qualification: '',
            registrationNumber: '',
            userId: '',
            locationIds: [],
            status: 'ACTIVE',
        },
    });

    const selectedLocationIds = watch('locationIds');

    const onSubmit = (data: DoctorFormData) => {
        // Prepare data for API - convert empty strings to undefined/null behavior if needed
        // but backend schema handles optional strings well.
        if (editingDoctor) {
            updateMutation.mutate({ id: editingDoctor.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleEdit = (doctor: any) => {
        setEditingDoctor(doctor);
        reset({
            name: doctor.name,
            qualification: doctor.qualification || '',
            registrationNumber: doctor.registrationNumber || '',
            userId: doctor.userId || '',
            locationIds: doctor.locationIds || [],
            status: doctor.status,
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingDoctor(null);
        reset({
            name: '',
            qualification: '',
            registrationNumber: '',
            userId: '',
            locationIds: [],
            status: 'ACTIVE',
        });
    };

    const toggleLocation = (locationId: string) => {
        const current = selectedLocationIds || [];
        const updated = current.includes(locationId)
            ? current.filter((id) => id !== locationId)
            : [...current, locationId];
        setValue('locationIds', updated);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Doctors</h1>
                    <p className="text-slate-600">Manage doctors and their assignments</p>
                </div>
                <button onClick={() => setIsModalOpen(true)} className="btn btn-primary btn-md">
                    <Plus className="w-4 h-4" />
                    Add Doctor
                </button>
            </div>

            {/* Doctors table */}
            <div className="card">
                {isLoading ? (
                    <div className="card-body">
                        <div className="animate-pulse space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                            ))}
                        </div>
                    </div>
                ) : doctors.length === 0 ? (
                    <div className="card-body text-center py-16">
                        <Stethoscope className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900">No doctors found</h3>
                        <p className="text-slate-500 mt-1">Add your first doctor to get started.</p>
                        <button onClick={() => setIsModalOpen(true)} className="btn btn-primary btn-md mt-4">
                            Add Doctor
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Qualification</th>
                                    <th>Reg. Number</th>
                                    <th>Linked User</th>
                                    <th>Locations</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {doctors.map((doctor: any) => {
                                    const linkedUser = users.find((u: any) => u.id === doctor.userId);
                                    return (
                                        <tr key={doctor.id}>
                                            <td className="font-medium">{doctor.name}</td>
                                            <td>{doctor.qualification || '-'}</td>
                                            <td>{doctor.registrationNumber || '-'}</td>
                                            <td>
                                                {linkedUser ? (
                                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                                        <User className="w-3 h-3 text-slate-400" />
                                                        {linkedUser.username}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400 text-xs italic">Not linked</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex flex-wrap gap-1">
                                                    {doctor.locationIds?.map((locId: string) => {
                                                        const loc = locations.find((l: any) => l.id === locId);
                                                        return loc ? (
                                                            <span key={locId} className="badge badge-sm bg-slate-100 text-slate-700 border-none">
                                                                {loc.name}
                                                            </span>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </td>
                                            <td>
                                                {doctor.status === 'ACTIVE' ? (
                                                    <span className="badge badge-success">Active</span>
                                                ) : (
                                                    <span className="badge badge-ghost">Inactive</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => handleEdit(doctor)}
                                                        className="btn btn-ghost btn-sm"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            if (confirm(`Are you sure you want to deactivate ${doctor.name}?`)) {
                                                                deleteMutation.mutate(doctor.id);
                                                            }
                                                        }}
                                                        className="btn btn-ghost btn-sm text-error-600"
                                                        title="Deactivate"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}
                            </h2>
                            <button onClick={handleCloseModal} className="p-1 hover:bg-slate-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Doctor Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.name ? 'input-error' : ''}`}
                                        placeholder="Dr. John Doe"
                                        {...register('name')}
                                    />
                                    {errors.name && <p className="error-text">{errors.name.message}</p>}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">Qualification</label>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="MBBS, MD"
                                            {...register('qualification')}
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Reg. Number</label>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="REG12345"
                                            {...register('registrationNumber')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="label">Link User Account (Optional)</label>
                                    <select className="input" {...register('userId')}>
                                        <option value="">No linked user</option>
                                        {users.map((user: any) => (
                                            <option key={user.id} value={user.id}>
                                                {user.fullName} ({user.username})
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Link this doctor profile to a system user account for login access.
                                    </p>
                                </div>

                                <div>
                                    <label className="label">Assigned Locations *</label>
                                    <div className={`border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto ${errors.locationIds ? 'border-error-500' : 'border-slate-200'}`}>
                                        {locations.map((loc: any) => (
                                            <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm checkbox-primary"
                                                    checked={selectedLocationIds?.includes(loc.id)}
                                                    onChange={() => toggleLocation(loc.id)}
                                                />
                                                <span className="text-sm">{loc.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.locationIds && <p className="error-text">{errors.locationIds.message}</p>}
                                </div>

                                <div>
                                    <label className="label">Status</label>
                                    <select className="input" {...register('status')}>
                                        <option value="ACTIVE">Active</option>
                                        <option value="INACTIVE">Inactive</option>
                                    </select>
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button type="button" onClick={handleCloseModal} className="btn btn-secondary btn-md">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createMutation.isPending || updateMutation.isPending
                                        ? 'Saving...'
                                        : editingDoctor
                                            ? 'Update Doctor'
                                            : 'Add Doctor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
